import { useEffect, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
    domainEventBus,
    ROI_CONTOUR_EDIT_EVENT_TYPES,
    RoiContourEditEvent,
} from '~/domain/events';
import type { Environment } from '~/engine/data/Environment';
import { useResidentEnvironments } from '~/hooks/environment/useResidentEnvironments';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import {
    activeStructurePeekState,
    currentStructurePeekState,
} from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';

/** Writes the peek onto one Environment, repainting its contours on a change. */
function publishPeek(environment: Environment, roiNumber: number | null): void {
    if (environment.peekedRoiNumber !== roiNumber) {
        environment.peekedRoiNumber = roiNumber;
        environment.triggerContourLayerUpdate();
    }
}

/**
 * Bridges the peek onto the engine: publishes the peeked ROI number on the
 * Environment of the active structure set, and none on every other resident
 * Environment, and ends the peek on every signal other than the release of
 * its key. Display-only — it writes `activeStructurePeekState` and the
 * engine's `peekedRoiNumber` and nothing else, so no ROI, selection or
 * contour changes and no domain event is published. Mount once in an
 * always-rendered viewer component that outlives the grid's tiles.
 *
 * The peek is ended — the atom cleared, not merely left unpublished — when:
 *  - it no longer names the current set's peekable structure (the selection
 *    moved, the set changed, the structure was hidden, deleted or became a
 *    point, or a bulk selection was engaged);
 *  - the peeked structure is edited, undone or redone, which the contour
 *    tools and undo refuse during a peek but another path may still do;
 *  - the window loses focus or the tab is hidden;
 *  - the hook unmounts.
 */
export function useActiveStructurePeek(): void {
    const { environment: target } = useActiveEditTarget();
    const residents = useResidentEnvironments();
    const peekHeld = useRecoilValue(activeStructurePeekState) !== null;
    const peekedRoiNumber =
        useRecoilValue(currentStructurePeekState)?.roiNumber ?? null;
    const setPeek = useSetRecoilState(activeStructurePeekState);
    const structureSetUid = useRecoilValue(currentStructureSetState)?.dicomUid;

    // Cleared, not masked: a value left in the atom would withhold the
    // structure again the moment it became peekable once more.
    useEffect(() => {
        if (peekHeld && peekedRoiNumber === null) {
            setPeek(null);
        }
    }, [peekHeld, peekedRoiNumber, setPeek]);

    // ROI numbers repeat across structure sets, so only the Environment that
    // draws the active set is told; another one's same-numbered structure
    // stays drawn.
    useEffect(() => {
        for (const environment of residents) {
            publishPeek(
                environment,
                environment === target ? peekedRoiNumber : null
            );
        }
    }, [residents, target, peekedRoiNumber]);

    // An edit that reaches the peeked structure anyway ends the peek, so
    // that no change to it stays unseen.
    useEffect(() => {
        if (peekedRoiNumber === null) {
            return;
        }
        const endPeekOnEdit = (event: RoiContourEditEvent): void => {
            if (
                event.roiNumber === peekedRoiNumber &&
                event.structureSetUid === structureSetUid
            ) {
                setPeek(null);
            }
        };
        const subscriptions = ROI_CONTOUR_EDIT_EVENT_TYPES.map((eventType) =>
            domainEventBus.subscribe(eventType, endPeekOnEdit)
        );
        return () => {
            subscriptions.forEach((subscription) => subscription.unsubscribe());
        };
    }, [peekedRoiNumber, structureSetUid, setPeek]);

    // Once the window loses focus or the tab is hidden, the release of the
    // key holding the peek may never be delivered, so either one ends it.
    useEffect(() => {
        const listeners = new AbortController();
        const { signal } = listeners;
        window.addEventListener('blur', () => setPeek(null), { signal });
        document.addEventListener(
            'visibilitychange',
            () => {
                if (document.visibilityState === 'hidden') {
                    setPeek(null);
                }
            },
            { signal }
        );
        return () => listeners.abort();
    }, [setPeek]);

    // After unmount nothing validates the peek or withdraws it from the
    // engine, so both end here: a teardown mid-peek cannot leave an
    // Environment withholding the structure, nor the atom naming it for the
    // next mount. The ref keeps this effect from re-running with the list.
    const residentsRef = useRef(residents);
    residentsRef.current = residents;
    useEffect(
        () => () => {
            setPeek(null);
            for (const environment of residentsRef.current) {
                publishPeek(environment, null);
            }
        },
        [setPeek]
    );
}
